import { ApiCampaignType, ApiSimpleCampaign } from "@incendium/api";
import { Autocomplete, TextField, OutlinedTextFieldProps } from "@mui/material";
import { useCampaigns } from "Hooks/useCampaigns";
import { useMemo } from "react";

interface ICampaignsDropdownProps
  extends Omit<OutlinedTextFieldProps, "onChange"> {
  value?: string | null;
  onChange: (name: string | null) => void;
  /** When set, only campaigns with one of these types are shown (e.g. A/B campaigns). */
  campaignTypes?: ApiCampaignType | ApiCampaignType[];
}

function CampaignsDropdown({
  label,
  size,
  variant,
  value,
  onChange,
  fullWidth,
  sx,
  campaignTypes,
}: ICampaignsDropdownProps) {
  const { campaigns } = useCampaigns();

  const filteredCampaigns = useMemo(() => {
    const list = campaigns || [];
    if (!campaignTypes) return list;
    const types = Array.isArray(campaignTypes) ? campaignTypes : [campaignTypes];
    return list.filter((c) => c.type != null && types.includes(c.type));
  }, [campaigns, campaignTypes]);

  const selected = useMemo(
    () => filteredCampaigns.find((c) => c.name === value) ?? null,
    [filteredCampaigns, value]
  );

  const sortedCampaigns = useMemo(
    () =>
      [...filteredCampaigns].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
    [filteredCampaigns]
  );

  return (
    <Autocomplete<ApiSimpleCampaign>
      sx={sx}
      fullWidth={fullWidth}
      options={sortedCampaigns}
      value={selected}
      onChange={(_, v) => {
        onChange(v?.name ?? null);
      }}
      getOptionLabel={(o: ApiSimpleCampaign) => o.name || ""}
      renderInput={(params) => (
        <TextField {...params} label={label} variant={variant} size={size} />
      )}
    />
  );
}

export default CampaignsDropdown;
